package com.partejar.app.feature.gestantes.presentation

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.animation.SizeTransform
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.animation.togetherWith
import androidx.compose.animation.core.snap
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.semantics.LiveRegionMode
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.liveRegion
import androidx.compose.ui.semantics.semantics
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarMotion
import com.partejar.app.core.ui.components.PartejarStatusBadge
import com.partejar.app.core.ui.components.PartejarStatusBadgeTone
import com.partejar.app.core.ui.components.PartejarTextField
import com.partejar.app.core.ui.components.reducedMotion
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.gestantes.data.GestanteComorbidity

private sealed interface ComorbidityPanel {
    data object List : ComorbidityPanel
    data object Add : ComorbidityPanel
    data class Edit(val item: GestanteComorbidity) : ComorbidityPanel
}

@Composable
internal fun ComorbidityManagementContent(
    comorbidities: List<GestanteComorbidity>,
    saving: Boolean,
    actionRevision: Long,
    actionError: String?,
    showingAddForm: Boolean,
    onShowingAddFormChange: (Boolean) -> Unit,
    onEditingChange: (Boolean) -> Unit,
    onAdd: (String, String?) -> Unit,
    onUpdate: (Long, String?) -> Unit,
    onDeactivate: (Long) -> Unit
) {
    val spacing = PartejarTheme.spacing
    val focus = LocalFocusManager.current
    val keyboard = LocalSoftwareKeyboardController.current
    var description by rememberSaveable { mutableStateOf("") }
    var observation by rememberSaveable { mutableStateOf("") }
    var editingId by rememberSaveable { mutableStateOf<Long?>(null) }
    var editedObservation by rememberSaveable { mutableStateOf("") }
    var pendingRevision by rememberSaveable { mutableStateOf<Long?>(null) }
    var pendingMessage by rememberSaveable { mutableStateOf("") }
    var feedback by rememberSaveable { mutableStateOf<String?>(null) }

    LaunchedEffect(showingAddForm) {
        if (showingAddForm) feedback = null
    }

    // A request being sent is not a successful save. Keep drafts and the editor on failure.
    LaunchedEffect(actionRevision, actionError, saving) {
        val submittedAt = pendingRevision ?: return@LaunchedEffect
        if (!saving && actionRevision > submittedAt) {
            feedback = pendingMessage
            pendingRevision = null
            description = ""
            observation = ""
            editingId = null
            onEditingChange(false)
            onShowingAddFormChange(false)
        } else if (!saving && actionError != null) {
            pendingRevision = null
        }
    }
    val editing = comorbidities.firstOrNull { it.id == editingId }
    val panel = when {
        showingAddForm -> ComorbidityPanel.Add
        editing != null -> ComorbidityPanel.Edit(editing)
        else -> ComorbidityPanel.List
    }
    val busy = saving || pendingRevision != null
    fun submit(message: String, action: () -> Unit) {
        focus.clearFocus()
        keyboard?.hide()
        pendingRevision = actionRevision
        pendingMessage = message
        feedback = null
        action()
    }
    Column(verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
        feedback?.let {
            Text(it, style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.semantics { liveRegion = LiveRegionMode.Polite })
        }
        ComorbidityTransition(panel) { target ->
            when (target) {
                ComorbidityPanel.List -> Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    if (comorbidities.isEmpty()) {
                        ComorbidityEmptyState()
                    } else {
                        Text("CONDIÇÕES CLÍNICAS", style = MaterialTheme.typography.labelLarge,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.semantics { heading() })
                        comorbidities.forEach { item ->
                            ComorbidityCard(item, !busy) {
                                feedback = null
                                editingId = item.id
                                editedObservation = item.observation.orEmpty()
                                onEditingChange(true)
                            }
                        }
                    }
                    ComorbidityAction("Adicionar comorbidade", PartejarIcons.Actions.add, !busy,
                        variant = PartejarButtonVariant.SECONDARY) {
                        feedback = null
                        onShowingAddFormChange(true)
                    }
                }
                ComorbidityPanel.Add -> ComorbidityForm(
                    item = null, description = description, observation = observation,
                    onDescription = { description = it }, onObservation = { observation = it },
                    busy = busy, actionError = actionError,
                    onSave = { submit("Comorbidade adicionada.") { onAdd(description, observation.ifBlank { null }) } },
                    onCancel = {
                        focus.clearFocus(); keyboard?.hide()
                        description = ""; observation = ""; onShowingAddFormChange(false)
                    },
                    onDeactivate = {}
                )
                is ComorbidityPanel.Edit -> ComorbidityForm(
                    item = target.item, description = target.item.description, observation = editedObservation,
                    onDescription = {}, onObservation = { editedObservation = it },
                    busy = busy, actionError = actionError,
                    onSave = { submit("Observação atualizada.") { onUpdate(target.item.id, editedObservation.ifBlank { null }) } },
                    onCancel = { focus.clearFocus(); keyboard?.hide(); editingId = null; onEditingChange(false) },
                    onDeactivate = { submit("Condição inativada e mantida no histórico.") { onDeactivate(target.item.id) } }
                )
            }
        }
    }
}

@Composable
private fun ComorbidityCard(item: GestanteComorbidity, enabled: Boolean, onEdit: () -> Unit) {
    val content: @Composable () -> Unit = {
        Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)) {
            Row(horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12),
                verticalAlignment = Alignment.Top) {
                Text(item.description, modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.titleLarge,
                    color = if (item.active) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onSurfaceVariant)
                ComorbidityStatus(item.active)
            }
            ComorbidityDivider()
            Text(item.observation?.takeIf { it.isNotBlank() } ?: "Sem observação registrada.",
                style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            if (item.active) {
                Row(horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8),
                    verticalAlignment = Alignment.CenterVertically) {
                    Icon(PartejarIcons.Actions.edit, contentDescription = null,
                        modifier = Modifier.size(PartejarTheme.dimensions.iconSmall), tint = MaterialTheme.colorScheme.primary)
                    Text("Editar observação", style = MaterialTheme.typography.labelLarge, color = MaterialTheme.colorScheme.primary)
                }
            }
        }
    }
    if (item.active) {
        PartejarCard(onClick = onEdit, modifier = Modifier.fillMaxWidth(), enabled = enabled,
            motionEnabled = true, content = content)
    } else {
        PartejarCard(modifier = Modifier.fillMaxWidth(), content = content)
    }
}

@Composable
private fun ComorbidityStatus(active: Boolean) {
    PartejarStatusBadge(if (active) "Ativa" else "Inativa",
        tone = if (active) PartejarStatusBadgeTone.PRIMARY else PartejarStatusBadgeTone.NEUTRAL)
}

@Composable
private fun ComorbidityEmptyState() {
    PartejarCard(Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)) {
            Icon(PartejarIcons.Navigation.prontuario, contentDescription = null,
                modifier = Modifier.size(PartejarTheme.dimensions.iconLarge), tint = MaterialTheme.colorScheme.primary)
            Text("Nenhuma condição registrada", style = MaterialTheme.typography.titleLarge)
            Text("Adicione as comorbidades informadas para organizar o acompanhamento clínico.",
                style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
    }
}

@Composable
private fun ComorbidityForm(
    item: GestanteComorbidity?, description: String, observation: String,
    onDescription: (String) -> Unit, onObservation: (String) -> Unit,
    busy: Boolean, actionError: String?, onSave: () -> Unit, onCancel: () -> Unit, onDeactivate: () -> Unit
) {
    var confirmingDeactivation by rememberSaveable(item?.id) { mutableStateOf(false) }
    val spacing = PartejarTheme.spacing
    PartejarCard(Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
            Text(if (item == null) "Adicionar comorbidade" else "Editar observação",
                style = MaterialTheme.typography.titleLarge, modifier = Modifier.semantics { heading() })
            if (item == null) {
                PartejarTextField(description, onDescription, label = "Descrição da condição",
                    enabled = !busy, singleLine = false, maxLines = 4)
            } else {
                Text(description, style = MaterialTheme.typography.titleMedium)
            }
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically) {
                Text("Estado", style = MaterialTheme.typography.labelLarge, color = MaterialTheme.colorScheme.onSurfaceVariant)
                ComorbidityStatus(item?.active ?: true)
            }
            Text(if (item == null) "A condição será registrada como ativa." else "O nome da condição é mantido. Você pode atualizar a observação ou inativar o registro.",
                style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            ComorbidityDivider()
            PartejarTextField(observation, onObservation, label = "Observação (opcional)",
                enabled = !busy, singleLine = false, minLines = 3, maxLines = 6)
            actionError?.let {
                PartejarAlert("Não foi possível concluir", PartejarAlertTone.ERROR,
                    modifier = Modifier.semantics { liveRegion = LiveRegionMode.Polite }) { Text(it) }
            }
            ComorbidityAction(if (item == null) "Salvar comorbidade" else "Salvar observação",
                PartejarIcons.Feedback.success, !busy && !confirmingDeactivation && description.isNotBlank(),
                loading = busy && !confirmingDeactivation, onClick = onSave)
            ComorbidityAction("Cancelar", PartejarIcons.Actions.back, !busy,
                variant = PartejarButtonVariant.SECONDARY, onClick = onCancel)
            if (item?.active == true) {
                ComorbidityDivider()
                ComorbidityTransition(confirmingDeactivation) { confirming ->
                    if (confirming) {
                        Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                            Text("Inativar esta condição?", style = MaterialTheme.typography.titleMedium)
                            Text("Ela deixará a lista de condições ativas. A reativação não está disponível neste fluxo.",
                                style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                            ComorbidityAction("Confirmar inativação", PartejarIcons.Actions.edit, !busy,
                                loading = busy, variant = PartejarButtonVariant.SECONDARY, onClick = onDeactivate)
                            ComorbidityAction("Manter ativa", PartejarIcons.Actions.back, !busy,
                                variant = PartejarButtonVariant.TERTIARY) { confirmingDeactivation = false }
                        }
                    } else {
                        ComorbidityAction("Inativar condição", PartejarIcons.Actions.edit, !busy,
                            variant = PartejarButtonVariant.TERTIARY) { confirmingDeactivation = true }
                    }
                }
            }
        }
    }
}

@Composable
private fun ComorbidityAction(
    text: String, icon: ImageVector, enabled: Boolean, loading: Boolean = false,
    variant: PartejarButtonVariant = PartejarButtonVariant.PRIMARY, onClick: () -> Unit
) {
    PartejarButton(text, onClick, enabled = enabled, isLoading = loading, variant = variant,
        motionEnabled = true, loadingDescription = "Salvando",
        leadingIcon = { Icon(icon, contentDescription = null, modifier = Modifier.size(PartejarTheme.dimensions.iconSmall)) })
}

@Composable
private fun ComorbidityDivider() {
    HorizontalDivider(thickness = PartejarTheme.dimensions.strokeThin, color = MaterialTheme.colorScheme.outlineVariant)
}

/** One content group moves a spacing token; no cascade, bounce or ambient animation. */
@Composable
private fun <T> ComorbidityTransition(target: T, content: @Composable (T) -> Unit) {
    val reduced = reducedMotion()
    val offset = with(LocalDensity.current) { PartejarTheme.spacing.space12.roundToPx() }
    AnimatedContent(targetState = target, label = "comorbidityPanel",
        contentKey = { if (it is ComorbidityPanel.Edit) it.item.id else it }, transitionSpec = {
        val enter = if (reduced) EnterTransition.None else
            fadeIn(tween(PartejarMotion.entrance, easing = PartejarMotion.enter)) +
                slideInVertically(tween(PartejarMotion.entrance, easing = PartejarMotion.enter)) { offset }
        val exit = if (reduced) ExitTransition.None else
            fadeOut(tween(PartejarMotion.departure, easing = PartejarMotion.exit)) +
                slideOutVertically(tween(PartejarMotion.departure, easing = PartejarMotion.exit)) { offset }
        (enter togetherWith exit).using(SizeTransform(clip = false) { _, _ ->
            if (reduced) snap() else tween(PartejarMotion.entrance, easing = PartejarMotion.calm)
        })
    }) { content(it) }
}
